import { http } from "msw";
import type { Notification, Order, Page, Review } from "../../api/types";
import { getCollection } from "../db";
import { paginateCursor, sortBy } from "../engine";
import { decorateCreatorSummary, findViewerStoreSlug, mockJson } from "./shared";

let cachedNotifications: Notification[] | null = null;

async function buildNotifications(): Promise<Notification[]> {
  if (cachedNotifications) return cachedNotifications;

  const storeSlug = await findViewerStoreSlug();
  const [orders, reviews] = await Promise.all([getCollection<Order>("orders"), getCollection<Review>("reviews")]);

  const orderNotifications: Notification[] = orders
    .filter((o) => o.storeSlug === storeSlug && o.status === "completed")
    .slice(0, 20)
    .map((o) => ({
      id: `notif-order-${o.id}`,
      kind: "order",
      actor: decorateCreatorSummary(o.buyer),
      message: `${o.buyer.displayName} purchased ${o.product.title}`,
      read: false,
      createdAt: o.createdAt,
      href: "/dashboard/orders",
    }));

  const reviewNotifications: Notification[] = reviews.slice(0, 15).map((r) => ({
    id: `notif-review-${r.id}`,
    kind: "review",
    actor: decorateCreatorSummary(r.author),
    message: `${r.author.displayName} left a ${r.rating}-star review`,
    read: true,
    createdAt: r.createdAt,
  }));

  cachedNotifications = sortBy([...orderNotifications, ...reviewNotifications], (n) => -Date.parse(n.createdAt));
  return cachedNotifications;
}

export const notificationsHandlers = [
  http.get("/api/notifications", async ({ request }) => {
    const url = new URL(request.url);
    const cursor = url.searchParams.get("cursor");
    const limit = Number(url.searchParams.get("limit") ?? 20) || 20;
    const notifications = await buildNotifications();
    const page = paginateCursor(notifications, cursor, limit);
    const body: Page<Notification> = { items: page.items, nextCursor: page.nextCursor };
    return mockJson(body);
  }),
];
